const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const getAnonId = () => {
  let id = localStorage.getItem('aurra_uid');
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2));
    localStorage.setItem('aurra_uid', id);
  }
  return id;
};

export const formatDetail = (detail) => {
  if (detail == null) return 'Une erreur est survenue';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail.msg) return detail.msg;
  if (detail.code) return detail.code;
  return String(detail);
};

export class ApiError extends Error {
  constructor(status, detail) { super(formatDetail(detail)); this.status = status; this.detail = detail; }
}

const parseError = async (res) => {
  let detail = res.statusText;
  try { detail = (await res.json()).detail ?? detail; } catch (e) { console.warn(`[api] non-JSON error body for ${res.url} (${res.status})`, e); }
  return new ApiError(res.status, detail);
};

let refreshing = null;
const tryRefresh = () => {
  if (!refreshing) {
    refreshing = fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' }).then((r) => r.ok).catch(() => false).finally(() => { refreshing = null; });
  }
  return refreshing;
};

const rawFetch = (path, opts = {}, json = true) => fetch(`${BASE}${path}`, {
  ...opts,
  credentials: 'include',
  headers: { ...(json ? { 'Content-Type': 'application/json' } : {}), 'X-Anon-Id': getAnonId(), ...(opts.headers || {}) },
});

const req = async (path, opts = {}, json = true) => {
  let res = await rawFetch(path, opts, json);
  if (res.status === 401 && !path.startsWith('/auth/')) {
    if (await tryRefresh()) res = await rawFetch(path, opts, json);
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return null;
  return res.json();
};

export const api = {
  me: () => req('/auth/me'),
  register: (body) => req('/auth/register', { method: 'POST', body: JSON.stringify({ ...body, origin_url: window.location.origin }) }),
  login: (body) => req('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  googleSession: (session_id) => req('/auth/session', { method: 'POST', body: JSON.stringify({ session_id }) }),
  logout: () => req('/auth/logout', { method: 'POST' }),
  listAgents: () => req('/agents'),
  createAgent: (body) => req('/agents', { method: 'POST', body: JSON.stringify(body) }),
  updateAgent: (id, body) => req(`/agents/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteAgent: (id) => req(`/agents/${id}`, { method: 'DELETE' }),
  listConversations: () => req('/conversations'),
  getConversation: (id) => req(`/conversations/${id}`),
  deleteConversation: (id) => req(`/conversations/${id}`, { method: 'DELETE' }),
  patchConversation: (id, body) => req(`/conversations/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  searchConversations: (q) => req(`/conversations/search?q=${encodeURIComponent(q)}`),
  listFolders: () => req('/folders'),
  createFolder: (name) => req('/folders', { method: 'POST', body: JSON.stringify({ name }) }),
  renameFolder: (id, name) => req(`/folders/${id}`, { method: 'PUT', body: JSON.stringify({ name }) }),
  deleteFolder: (id) => req(`/folders/${id}`, { method: 'DELETE' }),
  upload: (file) => { const fd = new FormData(); fd.append('file', file); return req('/upload', { method: 'POST', body: fd }, false); },
  plans: () => req('/payments/plans'),
  checkout: (interval) => req('/payments/checkout', { method: 'POST', body: JSON.stringify({ interval, origin_url: window.location.origin }) }),
  paymentStatus: (sessionId) => req(`/payments/status/${sessionId}`),
  cancelSubscription: () => req('/payments/cancel', { method: 'POST' }),
  forgotPassword: (email) => req('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email, origin_url: window.location.origin }) }),
  resetPassword: (token, password) => req('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }),
  shareConversation: (id) => req(`/conversations/${id}/share`, { method: 'POST' }),
  unshareConversation: (id) => req(`/conversations/${id}/share`, { method: 'DELETE' }),
  getShared: (shareId) => req(`/shared/${shareId}`),
  adminStats: () => req('/admin/stats'),
  transcribe: (blob, language) => { const fd = new FormData(); fd.append('file', blob, 'audio.webm'); if (language) fd.append('language', language); return req('/voice/transcribe', { method: 'POST', body: fd }, false); },
  tts: (text, voice) => req('/voice/tts', { method: 'POST', body: JSON.stringify({ text, voice }) }),
  voices: () => req('/voice/voices'),
  listImages: () => req('/images'),
  deleteImage: (id) => req(`/images/${id}`, { method: 'DELETE' }),
};

export const apiUrl = (path) => `${BASE}${path.replace(/^\/api/, '')}`;

export const fileUrl = (id) => `${BASE}/files/${id}`;

// Streams SSE events from /chat/stream. onEvent receives parsed JSON objects.
export const streamChat = async (body, onEvent, signal) => {
  let res = await rawFetch('/chat/stream', { method: 'POST', body: JSON.stringify(body), signal });
  if (res.status === 401 && await tryRefresh()) res = await rawFetch('/chat/stream', { method: 'POST', body: JSON.stringify(body), signal });
  if (!res.ok) throw await parseError(res);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop();
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (line) onEvent(JSON.parse(line.slice(6)));
    }
  }
};
